// Coupon management redesign (Phase 2 PR 5, 2026-05-15):
//   coupon_batch_codes batch 入稿 sheet (CSV or 直接入力 + プレビュー + 重複検出)。
//
// Spec: design/coupon-management-redesign-spec.md §CMS 入稿 UX §2 Code 入稿
//
// auth: backend (CouponTemplatesService.addCodes) が template owner check 済。
//   CMS 側 sheet は template owner にしか表示されない (= 一覧 filter で見える template のみ操作可)。
//
// 入稿フロー:
//   1. CSV file or textarea 入力 → parse → preview 表示
//   2. on_duplicate (skip/reject/error) + default_valid_to を設定
//   3. 「投入」 → POST → response (inserted/skipped/rejected) 表示
//   4. 確定で sheet 閉じる、parent (= 一覧ページ) の reload を呼ぶ

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Textarea } from '@/components/common/Textarea';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Upload } from 'lucide-react';

type OnDuplicate = 'skip' | 'reject' | 'error';

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    template: any | null,
    onSuccess?: () => void
}

// PR 2 backend BATCH_CODES_MAX = 10000 (CouponTemplatesService.js:55)。
const BATCH_CODES_MAX = 10000;

// CSV / textarea を行ごとにパースする。
// - 1 列 (= code のみ) or 2 列 (= code,valid_to) を許容。
// - ヘッダ行は optional (= 1 行目が "code" / "code,valid_to" なら自動 skip)。
// - 各 code は 1-64 chars (backend 制約と一致、CouponTemplatesService.js:353-360)。
function parseCodesText(text: string): { codes: string[], rows: { code: string, valid_to: string | null }[], errors: string[] } {
    const errors: string[] = [];
    const codes: string[] = [];
    const rows: { code: string, valid_to: string | null }[] = [];

    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
    let startIdx = 0;
    if (lines.length > 0) {
        const head = lines[0].toLowerCase();
        if (head === 'code' || head === 'code,valid_to') {
            startIdx = 1;
        }
    }
    for (let i = startIdx; i < lines.length; i++) {
        const line = lines[i];
        const cols = line.split(',').map(c => c.trim());
        const code = cols[0];
        const validTo = cols.length >= 2 && cols[1] ? cols[1] : null;
        if (!code) {
            errors.push(`行 ${i + 1}: code が空です`);
            continue;
        }
        if (code.length > 64) {
            errors.push(`行 ${i + 1}: code が 64 文字を超えています ("${code.slice(0, 16)}…")`);
            continue;
        }
        codes.push(code);
        rows.push({ code, valid_to: validTo });
    }
    return { codes, rows, errors };
}

export default observer(function CouponBatchCodeUploadSheet({ open, onOpenChange, template, onSuccess }: Props) {
    const {
        couponTemplateStore: { addCodes }
    } = useStore();

    const [inputMode, setInputMode] = useState<'csv' | 'text'>('text');
    const [rawText, setRawText] = useState('');
    const [onDuplicate, setOnDuplicate] = useState<OnDuplicate>('skip');
    const [defaultValidTo, setDefaultValidTo] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<{ inserted: number, skipped: number, rejected: string[], total_issued_after: number } | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!open) {
            setRawText('');
            setOnDuplicate('skip');
            setDefaultValidTo('');
            setResult(null);
            setInputMode('text');
        }
    }, [open]);

    const parsed = useMemo(() => parseCodesText(rawText), [rawText]);
    const previewRows = parsed.rows.slice(0, 10);

    const handleFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
            const text = String(ev.target?.result ?? '');
            setRawText(text);
            setInputMode('csv');
        };
        reader.onerror = () => {
            toastify('error', 'CSV 読み込みに失敗しました');
        };
        reader.readAsText(file);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        // 同じファイルを再選択できるよう reset
        e.target.value = '';
    };

    const handleSubmit = async () => {
        if (!template?.id) {
            toastify('error', 'template が選択されていません');
            return;
        }
        if (parsed.codes.length === 0) {
            toastify('error', 'コードが 1 件も入力されていません');
            return;
        }
        if (parsed.codes.length > BATCH_CODES_MAX) {
            toastify('error', `1 回の入稿上限は ${BATCH_CODES_MAX} 件です (現状: ${parsed.codes.length})`);
            return;
        }
        if (parsed.errors.length > 0) {
            toastify('error', `パースエラーがあります (${parsed.errors.length} 件)。修正してください。`);
            return;
        }

        const validToIso = defaultValidTo ? new Date(defaultValidTo).toISOString() : null;

        setSubmitting(true);
        setResult(null);
        try {
            const res = await flowResult(addCodes({
                id: template.id,
                data: {
                    codes: parsed.codes,
                    default_valid_to: validToIso,
                    on_duplicate: onDuplicate
                }
            }));

            if (res?.ok) {
                setResult(res.data);
                toastify('success', `${res.data?.inserted ?? 0} 件投入、${res.data?.skipped ?? 0} 件スキップ`);
                onSuccess?.();
            } else {
                toastify('error', res?.message || '投入に失敗しました');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[640px] sm:max-w-[640px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>
                        コード入稿
                        {template?.name && (
                            <span className='text-sm font-normal text-gray-500 ml-2'>
                                (
                                {template.name}
                                )
                            </span>
                        )}
                    </SheetTitle>
                    <SheetDescription>
                        事業者 POS に登録済みのクーポンコードを batch 投入。
                        1 行 1 code、または「code,valid_to」 の 2 列形式。1 回の上限は
                        {' '}
                        {BATCH_CODES_MAX}
                        {' '}
                        件。
                    </SheetDescription>
                </SheetHeader>

                <div className='space-y-4 py-4'>
                    {/* 入力 mode tab */}
                    <div className='flex items-center gap-2'>
                        <Button
                            type='button'
                            variant={inputMode === 'text' ? 'active' : 'outline'}
                            size='sm'
                            onClick={() => setInputMode('text')}
                        >
                            直接入力
                        </Button>
                        <Button
                            type='button'
                            variant={inputMode === 'csv' ? 'active' : 'outline'}
                            size='sm'
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Upload className='w-4 h-4 mr-1' />
                            CSV を開く
                        </Button>
                        <input
                            ref={fileInputRef}
                            type='file'
                            accept='.csv,.txt,text/csv,text/plain'
                            onChange={handleFileInput}
                            className='hidden'
                        />
                    </div>

                    <div>
                        <Label>
                            コード一覧 (改行区切り、または code,valid_to の 2 列 CSV)
                        </Label>
                        <Textarea
                            value={rawText}
                            onChange={e => setRawText(e.target.value)}
                            rows={10}
                            className='font-mono text-xs'
                            placeholder={'ABCD-1234\nABCD-1235\nABCD-1236,2026-08-31T23:59:59+09:00'}
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                            読み取り済:
                            {' '}
                            {parsed.codes.length}
                            {' '}
                            件
                            {parsed.errors.length > 0 && (
                                <span className='text-red-600 ml-2'>
                                    /
                                    {' '}
                                    {parsed.errors.length}
                                    {' '}
                                    件エラー
                                </span>
                            )}
                        </p>
                    </div>

                    {parsed.errors.length > 0 && (
                        <div className='border border-red-300 bg-red-50 rounded p-2 text-xs space-y-1'>
                            <p className='font-semibold text-red-700'>パースエラー (修正してから再投入):</p>
                            {parsed.errors.slice(0, 5).map((err, i) => (
                                <p key={i} className='text-red-600'>{err}</p>
                            ))}
                            {parsed.errors.length > 5 && (
                                <p className='text-red-500'>
                                    ...他
                                    {' '}
                                    {parsed.errors.length - 5}
                                    {' '}
                                    件
                                </p>
                            )}
                        </div>
                    )}

                    {previewRows.length > 0 && (
                        <div>
                            <Label>プレビュー (先頭 10 件)</Label>
                            <div className='border border-gray-200 rounded mt-1 overflow-x-auto'>
                                <table className='w-full text-xs'>
                                    <thead>
                                        <tr className='bg-gray-50 text-left'>
                                            <th className='py-1 px-2'>#</th>
                                            <th className='py-1 px-2'>code</th>
                                            <th className='py-1 px-2'>valid_to (override)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((r, i) => (
                                            <tr key={i} className='border-t border-gray-100'>
                                                <td className='py-1 px-2 text-gray-400'>{i + 1}</td>
                                                <td className='py-1 px-2 font-mono'>{r.code}</td>
                                                <td className='py-1 px-2 font-mono text-gray-500'>{r.valid_to ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {parsed.rows.length > 10 && (
                                <p className='text-xs text-gray-500 mt-1'>
                                    ...他
                                    {' '}
                                    {parsed.rows.length - 10}
                                    {' '}
                                    件
                                </p>
                            )}
                        </div>
                    )}

                    <div>
                        <Label>個別 valid_to が指定されていない code の default 有効期限 (任意)</Label>
                        <Input
                            type='datetime-local'
                            value={defaultValidTo}
                            onChange={e => setDefaultValidTo(e.target.value)}
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                            未指定 = template の valid_to を継承。
                        </p>
                    </div>

                    <div>
                        <Label>重複検出時の挙動</Label>
                        <div className='flex flex-col gap-1 mt-1 text-sm'>
                            <label className='flex items-center gap-2'>
                                <input
                                    type='radio'
                                    name='on_duplicate'
                                    checked={onDuplicate === 'skip'}
                                    onChange={() => setOnDuplicate('skip')}
                                />
                                <span>
                                    <span className='font-medium'>skip</span>
                                    <span className='text-gray-500 ml-1'>(default): 既存 code は無視、新規だけ投入</span>
                                </span>
                            </label>
                            <label className='flex items-center gap-2'>
                                <input
                                    type='radio'
                                    name='on_duplicate'
                                    checked={onDuplicate === 'reject'}
                                    onChange={() => setOnDuplicate('reject')}
                                />
                                <span>
                                    <span className='font-medium'>reject</span>
                                    <span className='text-gray-500 ml-1'>: 1 件でも重複あれば全体を 422 で拒否</span>
                                </span>
                            </label>
                            <label className='flex items-center gap-2'>
                                <input
                                    type='radio'
                                    name='on_duplicate'
                                    checked={onDuplicate === 'error'}
                                    onChange={() => setOnDuplicate('error')}
                                />
                                <span>
                                    <span className='font-medium'>error</span>
                                    <span className='text-gray-500 ml-1'>: DB 制約違反を伝播 (= 500/422)、何も投入しない</span>
                                </span>
                            </label>
                        </div>
                    </div>

                    {result && (
                        <div className='border border-emerald-300 bg-emerald-50 rounded p-3 text-sm space-y-1'>
                            <p className='font-semibold text-emerald-800'>投入結果</p>
                            <p>
                                inserted:
                                <span className='font-mono ml-1'>{result.inserted}</span>
                                {' '}
                                / skipped:
                                <span className='font-mono ml-1'>{result.skipped}</span>
                                {' '}
                                / total_issued (累計):
                                <span className='font-mono ml-1'>{result.total_issued_after}</span>
                            </p>
                            {result.rejected.length > 0 && (
                                <p className='text-amber-700'>
                                    rejected:
                                    {' '}
                                    {result.rejected.slice(0, 5).join(', ')}
                                    {result.rejected.length > 5 && ` ...他 ${result.rejected.length - 5} 件`}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <SheetFooter>
                    <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                        閉じる
                    </Button>
                    <Button
                        type='button'
                        variant='active'
                        onClick={handleSubmit}
                        disabled={submitting || parsed.codes.length === 0 || parsed.errors.length > 0}
                    >
                        {submitting ? '投入中…' : `${parsed.codes.length} 件を投入`}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
});
